/**
 * Forward paper test of the hourly favorite rule pooled across three Kalshi
 * series (H2): on the hourly "above or below" contracts for ETH, BTC and SOL
 * (KXETHD, KXBTCD, KXSOLD), once a third or less of the hour remains, buy
 * the first side priced 85–95¢ and hold to settlement. One bet per series
 * per hour.
 *
 * The entry rule is the ETH hourly rule's (H1), unchanged and run by the
 * same code (`pickHourlyEntry`). What is different is the test around it:
 * three series so the bets come three times as fast, a sample size fixed in
 * advance from the size of the effect the research saw, and a bar that needs
 * the whole ~95% range above zero. See docs/HOURLY_POOLED_RULE.md.
 *
 * Nothing here trades: it records what the rule WOULD have bought and scores
 * it, on hours that did not exist when the test was registered. Pure logic:
 * no network, no storage.
 */
import {
  PRICE_RULE_STAKE_DOLLARS,
  netPerContract,
  stakePnl,
  type Side,
  type Verdict,
} from './btc15PriceRules';
import {
  ETH_HOURLY_RULE,
  NEXT_ASK_MAX_LAG_MINUTES,
  inEntryWindow,
  looseSideAsk,
  minuteOfHour,
  pickHourlyEntry,
  type HourlyMarketQuote,
} from './ethHourlyRule';
import type { ForwardRuleSummary } from './forwardRuleSummary';

export const POOLED_HOURLY_SERIES = ['KXETHD', 'KXBTCD', 'KXSOLD'] as const;
export type PooledHourlySeries = (typeof POOLED_HOURLY_SERIES)[number];

const isPooledSeries = (value: unknown): value is PooledHourlySeries =>
  POOLED_HOURLY_SERIES.includes(value as PooledHourlySeries);

export const POOLED_HOURLY_RULE = {
  id: 'H2',
  label: 'Hourly ETH, BTC and SOL: side at 85–95¢, last third of the hour',
  // The band, window and spread limit are H1's own, so the two can't drift.
  askLow: ETH_HOURLY_RULE.askLow,
  askHigh: ETH_HOURLY_RULE.askHigh,
  maxFractionLeft: ETH_HOURLY_RULE.maxFractionLeft,
  maxSpread: ETH_HOURLY_RULE.maxSpread,
  /** Only hours closing after this instant count (its registration time). */
  registeredAtMs: Date.parse('2026-10-09T00:00:00Z'),
} as const;

export interface PooledHourlyBar {
  /** Settled bets the verdict is read on, once. */
  bets: number;
  /** Net per contract after fees must exceed this... */
  minNetPerContract: number;
  /** ...and so must the lower end of its ~95% range (dollars). */
  minNetLowerBound: number;
}

/**
 * The pass bar, fixed before the test began.
 *
 * `bets` is how many settled bets the verdict is read on, and it is read
 * once: on exactly the first `bets` settled bets in order of close time,
 * never earlier and never again. It is sized for the effect the research
 * saw (about +1¢ a contract in 2026): replaying 2026's days at that edge,
 * 8,000 bets passes about 80% of the time, and passes about 3% of the time
 * when the true edge is zero. At roughly 60 bets a day that is a little over
 * four months.
 */
export const POOLED_HOURLY_BAR: PooledHourlyBar = {
  bets: 8_000,
  minNetPerContract: 0,
  minNetLowerBound: 0,
};

export interface PooledHourlyDecision {
  series: PooledHourlySeries;
  eventTicker: string;
  marketTicker: string;
  hourCloseMs: number;
  observedAtMs: number;
  /** Whole minutes into the hour (0-59). */
  minute: number;
  side: Side;
  ask: number;
  /** The same side's ask at the first run after the entry: a realistic-fill stress. */
  nextAsk?: number | null;
  /** Whole minutes between the entry and the `nextAsk` reading (1 or 2). */
  nextAskLagMinutes?: number;
  result?: Side;
}

export interface PooledHourlyState {
  schemaVersion: 1;
  decisions: PooledHourlyDecision[];
  lastRunMs: number | null;
}

export function emptyPooledHourlyState(): PooledHourlyState {
  return { schemaVersion: 1, decisions: [], lastRunMs: null };
}

/** About 60 decisions a day: the 8,000 the verdict needs, with room to keep logging. */
export const MAX_POOLED_HOURLY_DECISIONS = 30_000;

export function parsePooledHourlyState(raw: string): PooledHourlyState {
  try {
    const parsed = JSON.parse(raw) as Partial<PooledHourlyState>;
    if (parsed?.schemaVersion !== 1 || !Array.isArray(parsed.decisions)) {
      return emptyPooledHourlyState();
    }
    return {
      schemaVersion: 1,
      decisions: parsed.decisions.filter((d) => isPooledSeries(d?.series)),
      lastRunMs: typeof parsed.lastRunMs === 'number' ? parsed.lastRunMs : null,
    };
  } catch {
    return emptyPooledHourlyState();
  }
}

const mineThisHour = (
  state: PooledHourlyState,
  series: PooledHourlySeries,
  closeMs: number,
): PooledHourlyDecision | undefined =>
  state.decisions.find((d) => d.series === series && d.hourCloseMs === closeMs);

/**
 * True when this run needs `series`'s live quotes: an entry is possible, or
 * a next ask is owed and can still be taken.
 */
export function needsPooledQuotes(
  state: PooledHourlyState,
  series: PooledHourlySeries,
  nowMs: number,
  closeMs: number,
): boolean {
  if (closeMs <= POOLED_HOURLY_RULE.registeredAtMs) return false;
  const mine = mineThisHour(state, series, closeMs);
  if (!mine) return inEntryWindow(nowMs, closeMs);
  return (
    mine.nextAsk === undefined &&
    minuteOfHour(nowMs, closeMs) - mine.minute <= NEXT_ASK_MAX_LAG_MINUTES
  );
}

/**
 * Records one minute's quotes for one series: the next ask for an entry made
 * one or two minutes ago, then at most one new entry for this hour.
 */
export function observePooledHourly(
  state: PooledHourlyState,
  obs: {
    series: PooledHourlySeries;
    nowMs: number;
    closeMs: number;
    markets: readonly HourlyMarketQuote[];
  },
): PooledHourlyState {
  if (obs.closeMs <= POOLED_HOURLY_RULE.registeredAtMs) return state;
  const minute = minuteOfHour(obs.nowMs, obs.closeMs);
  const byTicker = new Map(obs.markets.map((m) => [m.ticker, m]));
  let changed = false;
  const decisions = state.decisions.map((d) => {
    if (d.series !== obs.series || d.hourCloseMs !== obs.closeMs) return d;
    if (d.nextAsk !== undefined) return d;
    const lag = minute - d.minute;
    if (lag < 1 || lag > NEXT_ASK_MAX_LAG_MINUTES) return d;
    const market = byTicker.get(d.marketTicker);
    changed = true;
    return {
      ...d,
      nextAsk: market ? looseSideAsk(market, d.side) : null,
      nextAskLagMinutes: lag,
    };
  });
  const withNext = changed ? { ...state, decisions } : state;
  if (
    mineThisHour(state, obs.series, obs.closeMs) ||
    !inEntryWindow(obs.nowMs, obs.closeMs)
  ) {
    return withNext;
  }
  const entry = pickHourlyEntry(
    obs.markets.filter((m) => Math.abs(m.closeMs - obs.closeMs) <= 90_000),
  );
  if (!entry) return withNext;
  return {
    ...state,
    decisions: [
      ...decisions,
      {
        series: obs.series,
        ...entry,
        hourCloseMs: obs.closeMs,
        observedAtMs: obs.nowMs,
        minute,
      },
    ].slice(-MAX_POOLED_HOURLY_DECISIONS),
  };
}

/** Applies Kalshi's official results to closed, unsettled decisions. */
export function settlePooledHourly(
  state: PooledHourlyState,
  resultsByTicker: ReadonlyMap<string, Side>,
  nowMs: number,
): PooledHourlyState {
  let changed = false;
  const decisions = state.decisions.map((d) => {
    if (d.result !== undefined || d.hourCloseMs >= nowMs - 60_000) return d;
    const result = resultsByTicker.get(d.marketTicker);
    if (d.nextAsk !== undefined && !result) return d;
    changed = true;
    const withNext = d.nextAsk === undefined ? { ...d, nextAsk: null } : d;
    return result ? { ...withNext, result } : withNext;
  });
  return changed ? { ...state, decisions } : state;
}

const DAY_MS = 86_400_000;

/**
 * Mean and standard error of per-bet results, with the error clustered by
 * the UTC day the bet settled. Bets on the same day share a market mood, so
 * treating them as independent makes the range too narrow; the research
 * this rule came from clusters the same way.
 */
export function clusteredMean(
  bets: readonly { hourCloseMs: number; net: number }[],
): { mean: number; se: number | null; days: number } | null {
  const n = bets.length;
  if (!n) return null;
  const mean = bets.reduce((a, b) => a + b.net, 0) / n;
  const byDay = new Map<number, number>();
  for (const b of bets) {
    const day = Math.floor(b.hourCloseMs / DAY_MS);
    byDay.set(day, (byDay.get(day) ?? 0) + (b.net - mean));
  }
  const days = byDay.size;
  if (days < 2) return { mean, se: null, days };
  let sumSq = 0;
  for (const s of byDay.values()) sumSq += s * s;
  return { mean, se: Math.sqrt((sumSq * days) / (days - 1)) / n, days };
}

export interface PooledSeriesSummary {
  settled: number;
  wins: number;
  winRate: number | null;
  avgAsk: number | null;
  netPerContract: number | null;
}

export interface PooledHourlySummary extends ForwardRuleSummary {
  /** Settled bets the verdict will be read on (the bar's `bets`). */
  target: number;
  /** Every settled bet, including any after the verdict was read. */
  settledAll: number;
  /** UTC days among the counted bets. */
  days: number;
  /** Per series, over the counted bets. Descriptive only. */
  bySeries: Record<PooledHourlySeries, PooledSeriesSummary>;
}

const seriesOrder = (s: PooledHourlySeries) => POOLED_HOURLY_SERIES.indexOf(s);

/**
 * Scores the rule against its pre-registered bar. Everything reported is
 * over the "counted" bets: the settled bets in order of close time, up to
 * the bar's `bets`. Until that many have settled the verdict is
 * "collecting"; from then on the counted set stops growing, so the verdict
 * is read once and later bets cannot change it.
 */
export function summarizePooledHourly(
  decisions: readonly PooledHourlyDecision[],
  bar: PooledHourlyBar = POOLED_HOURLY_BAR,
  stake = PRICE_RULE_STAKE_DOLLARS,
): PooledHourlySummary {
  const settledAll = decisions
    .filter((d) => d.result !== undefined)
    .sort(
      (a, b) =>
        a.hourCloseMs - b.hourCloseMs ||
        seriesOrder(a.series) - seriesOrder(b.series) ||
        a.marketTicker.localeCompare(b.marketTicker),
    );
  const counted = settledAll.slice(0, bar.bets);
  const n = counted.length;
  const won = (d: PooledHourlyDecision) => d.result === d.side;
  const stats = clusteredMean(
    counted.map((d) => ({
      hourCloseMs: d.hourCloseMs,
      net: netPerContract(d.ask, won(d)),
    })),
  );
  const net = stats ? stats.mean : null;
  const se = stats ? stats.se : null;
  const netLow = net !== null && se !== null ? net - 1.96 * se : null;
  const wins = counted.filter(won).length;

  let dollars = 0;
  let dollarsNext = 0;
  let withNext = 0;
  for (const d of counted) {
    dollars += stakePnl(d.ask, won(d), stake) ?? 0;
    if (d.nextAsk !== undefined && d.nextAsk !== null) {
      withNext++;
      dollarsNext += stakePnl(Math.max(d.ask, d.nextAsk), won(d), stake) ?? 0;
    }
  }

  const bySeries = {} as Record<PooledHourlySeries, PooledSeriesSummary>;
  for (const series of POOLED_HOURLY_SERIES) {
    const xs = counted.filter((d) => d.series === series);
    const w = xs.filter(won).length;
    bySeries[series] = {
      settled: xs.length,
      wins: w,
      winRate: xs.length ? w / xs.length : null,
      avgAsk: xs.length ? xs.reduce((a, d) => a + d.ask, 0) / xs.length : null,
      netPerContract: xs.length
        ? xs.reduce((a, d) => a + netPerContract(d.ask, won(d)), 0) / xs.length
        : null,
    };
  }

  const verdict: Verdict =
    n < bar.bets
      ? 'collecting'
      : net !== null &&
          net > bar.minNetPerContract &&
          netLow !== null &&
          netLow > bar.minNetLowerBound
        ? 'passing'
        : 'failing';

  return {
    rule: POOLED_HOURLY_RULE.id,
    label: POOLED_HOURLY_RULE.label,
    entered: decisions.length,
    settled: n,
    wins,
    winRate: n ? wins / n : null,
    avgAsk: n ? counted.reduce((a, d) => a + d.ask, 0) / n : null,
    netPerContract: net,
    netLow,
    netHigh: net !== null && se !== null ? net + 1.96 * se : null,
    stakePnl: Number(dollars.toFixed(2)),
    stakePnlNextFill: Number(dollarsNext.toFixed(2)),
    withNextFill: withNext,
    verdict,
    target: bar.bets,
    settledAll: settledAll.length,
    days: stats ? stats.days : 0,
    bySeries,
  };
}
